import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, getApiErrorMessage } from "../api/client";
import { toast } from "sonner";
import { Plus, X, Loader2, Truck, Building2, CalendarDays, ListChecks } from "lucide-react";
import { PageHeaderBand } from "../components/layout/PageHeaderBand";
import { formatCalendarDate, todayCalendarDate } from "../lib/calendar-date";
import { useTenantTimeZone } from "../hooks/useTenantTimeZone";
import { LOAD_TYPE_OPTIONS, getLoadTypeLabel, type LoadType } from "../constants/load-types";
import type { LoadCreateBody } from "../types/load";
import FleetUnitPicker, { fleetUnitLabel, useFleetUnit } from "../components/trucking/FleetUnitPicker";
import DriverPicker, { driverOptionLabel, loadWriteRefusal, useDriverOption } from "../components/trucking/DriverPicker";
import LoadRatesFields from "../components/trucking/LoadRatesFields";
import { regionName } from "../components/ProvinceSelect";
import { BILLING, EMPTY_RATES, invalidField, ratesCreateFields, readRefusals, type RatesDraft } from "../lib/load-rates";
import { Tooltip } from '../components/ui/tooltip';

/**
 * `load_type` is a Postgres ENUM, so this list is closed and lives in
 * src/constants/load-types.ts next to the values themselves.
 *
 * Eighteen labels used to be declared here, along with a "+ Add Custom Load
 * Type" button. NINE of the eighteen have no enum value — Hiboy, Winch
 * Tractor, Bed Truck, Tandem, Combo, Steamer, Acid, Frac, Rig Move — and a
 * custom type is a string the database cannot store under any spelling. None
 * of it was discoverable from the screen, because the screen never submitted
 * anything: Confirm booking called navigate("/trucking"). Now that it POSTs,
 * offering one of those would be a 500 on the enum cast.
 *
 * Adding a type is a migration on `load_type`, not a control on this form.
 */

// Customers loaded from API
const departureTimes = ["05:00", "05:30", "06:00", "06:30", "07:00", "07:30", "08:00", "08:30", "09:00", "10:00", "11:00", "12:00"];
const durations = ["2 hrs", "4 hrs", "6 hrs", "8 hrs", "10 hrs", "12 hrs"];

const steps = [
  { num: 1, label: "Job details" },
  { num: 2, label: "Cargo" },
  { num: 3, label: "Truck & rates" },
  { num: 4, label: "Confirm" },
];

/**
 * "6 hrs" is a label, `est_duration_hours` is NUMERIC. Returns undefined
 * rather than 0 for anything unparseable — an unknown duration is not a
 * zero-hour job.
 */
function parseDurationHours(label: string): number | undefined {
  const n = parseFloat(label);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The cargo weight box is free text ("18,400 kg", "4 t", "900"), and the API
 * wants a number in `weightKg` with the unit beside it in `unit`.
 *
 * This is a lossy read of an input that should be two fields, and it is
 * reported as a product gap rather than papered over: a weight nobody can
 * parse comes back as `undefined`, never as 0, so the BOL shows a gap instead
 * of a confident zero on a load someone has to haul.
 */
function parseWeight(raw: string): { weightKg?: number; unit?: string } {
  const text = raw.trim();
  if (!text) return {};
  const n = parseFloat(text.replace(/,/g, ""));
  if (!Number.isFinite(n)) return {};
  const unit = text.replace(/[\d.,\s]/g, "").toLowerCase() || "kg";
  return { weightKg: n, unit };
}

const inputCls = "h-11 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none";
const selectCls = "h-11 w-full cursor-pointer rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";
const labelCls = "text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5";

const NewLoadBooking = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [booking, setBooking] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [savingCustomer, setSavingCustomer] = useState(false);

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then(r => {
      const d = r.data?.data ?? r.data ?? [];
      return Array.isArray(d) ? d : [];
    }),
  });
  const customers: any[] = customersData ?? [];
  // The default load date is the TENANT's today (companies.time_zone), not the
  // dispatcher's device's. '' only seeds the input when no zone is known; the
  // submit guard below already refuses an empty date.
  const timeZone = useTenantTimeZone();
  const [form, setForm] = useState({
    loadType: "float" as LoadType,
    /** The customer's UUID. The API wants `customerId`, not a name. */
    customerId: "",
    afeNumber: "",
    pickupLocation: "",
    deliveryLsd: "",
    date: todayCalendarDate(timeZone) ?? "",
    // Blank, and required (#815, operator decision 2026-10-01). These were
    // pre-selected as 06:00 and 6 hrs, so a booking where nobody chose a
    // departure still wrote one.
    departureTime: "",
    duration: "",
    specialInstructions: "",
    cargoItems: [{ item: "", weight: "" }],
    /** A `trucking_fleet` unit id, or "" for none. The truck is optional. */
    truckId: "",
    /** A trailer's `trucking_fleet` id, or "" for none (api#1045). Optional. */
    trailerId: "",
    /** A `trucking_drivers` id, or "" for none (fe#815). Optional. */
    driverId: "",
  });
  const selectedTruck = useFleetUnit(form.truckId, "truck");
  const selectedTrailer = useFleetUnit(form.trailerId, "trailer");
  const selectedDriver = useDriverOption(form.driverId);
  // Billing and rates (api#950). Every box optional: blank is "use the rate card".
  const [rates, setRates] = useState<RatesDraft>(EMPTY_RATES);

  /** Absent renders as an em dash, never as a blank or "Unknown". */
  const selectedCustomerName =
    customers.find((c: any) => c.id === form.customerId)?.name ?? "\u2014";

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCustomer = async () => {
    const name = newCustomerName.trim();
    if (!name) return;
    setSavingCustomer(true);
    try {
      // Select the row the server just made, by ID. Selecting the typed NAME
      // left the picker holding a value that is not a customer id, which the
      // load POST would 404 on.
      const created = await api.customers.create({ name });
      const body = created.data;
      const row = body && typeof body === 'object' && 'data' in body ? body.data : body;
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (row?.id) update("customerId", row.id);
      setNewCustomerName("");
      setShowAddCustomer(false);
    } catch (err) {
      // Selecting the name anyway put a customer on the booking that has no
      // row behind it — the load would reference a customer the API cannot
      // resolve. Keep the panel open with the typed name so it can be retried.
      toast.error(getApiErrorMessage(err, "Failed to create customer"));
    } finally {
      setSavingCustomer(false);
    }
  };

  /**
   * Book the load.
   *
   * This was `navigate("/trucking")`. No request, no record, no error — the
   * dispatcher filled in four steps, pressed Confirm booking, landed on the
   * dashboard and had every reason to believe a load existed. Seven buttons
   * across six screens lead here and this was the end of all of them.
   */
  const handleSubmit = async () => {
    if (!form.pickupLocation.trim() || !form.deliveryLsd.trim() || !form.date) {
      toast.error("Pickup, delivery and date are required.");
      return;
    }
    if (!form.departureTime || !form.duration) {
      toast.error("Choose a departure time and an estimated duration.");
      return;
    }
    const badRate = invalidField(rates);
    if (badRate) {
      toast.error(`${badRate} must be a number.`);
      return;
    }

    setBooking(true);
    try {
      const payload: LoadCreateBody = {
        loadType: form.loadType,
        pickupLocation: form.pickupLocation.trim(),
        deliveryLocation: form.deliveryLsd.trim(),
        // A calendar day, sent as the operator typed it. Round-tripping it
        // through `new Date()` is what turns an Alberta evening into tomorrow.
        scheduledDate: form.date,
        customerId: form.customerId || undefined,
        // A fleet unit id (api#860), sent only when one was picked.
        ...(form.truckId ? { truckId: form.truckId } : {}),
        ...(form.trailerId ? { trailerId: form.trailerId } : {}),
        // The API asks the licence rule of a named driver and answers 409
        // DRIVER_LICENCE_EXPIRED; the toast below shows its sentence as sent.
        ...(form.driverId ? { driverId: form.driverId } : {}),
        afeNumber: form.afeNumber.trim() || undefined,
        specialInstructions: form.specialInstructions.trim() || undefined,
        scheduledDeparture: form.departureTime || undefined,
        estDurationHours: parseDurationHours(form.duration),
        cargoItems: form.cargoItems
          .filter((c) => c.item.trim())
          .map((c) => ({
            description: c.item.trim(),
            ...parseWeight(c.weight),
          })),
        // Only what was typed; a blank is filled from the rate card.
        ...(ratesCreateFields(rates) as Partial<LoadCreateBody>),
      };

      const res = await api.trucking.loads.create(payload);
      const body = res.data;
      const load = body && typeof body === 'object' && 'data' in body ? body.data : body;

      queryClient.invalidateQueries({ queryKey: ['trucking-loads'] });
      // POST answers with the Drizzle row, so the number is `bolNumber`. This
      // read `bol_number` (the GET spelling) and never named the BOL.
      const bolNumber = load?.bolNumber ?? load?.bol_number;
      toast.success(bolNumber ? `Load booked \u2014 BOL ${bolNumber}` : "Load booked.");
      // To the new BOL, where its Rates panel shows what the card filled and
      // lists any kind the card had no rate for (`rate_card_refusals`). The
      // refusals ride in router state: the API returns them on this response only.
      if (load?.id) {
        navigate(`/trucking/bols/${load.id}`, { state: { rateCardRefusals: readRefusals(body) } });
      } else {
        navigate("/trucking/bols");
      }
    } catch (err) {
      // The form stays exactly as it is. A load the server refused is not a
      // load, and leaving for /trucking on a failure is how this screen came to
      // report success for four months without sending anything.
      toast.error(loadWriteRefusal(err, "Failed to book load"));
    } finally {
      setBooking(false);
    }
  };

  /**
   * The band carries the booking as it stands: what the right-hand "Booking
   * Summary" column used to repeat. That column was a fixed 380px, so at phone
   * width the form beside it had no room at all.
   */
  const dateLabel = formatCalendarDate(form.date, { weekday: "short", month: "short", day: "numeric" }) ?? "—";

  return (
    <div data-testid="new-load-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="New Load"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "Load Type", sub: "Being booked", tone: "accent", value: getLoadTypeLabel(form.loadType) },
          { icon: <Building2 className="size-4" aria-hidden="true" />, label: "Customer", sub: "Billed to", tone: "neutral", value: selectedCustomerName },
          { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: "Date", sub: "Scheduled", tone: "info", value: dateLabel },
          { icon: <ListChecks className="size-4" aria-hidden="true" />, label: "Step", sub: "Of the booking", tone: "success", value: `${step} of ${steps.length}` },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Step indicator */}
          <ol aria-label="Booking steps" className="scrollbar-hide flex items-center gap-2 overflow-x-auto">
            {steps.map((s, i) => (
              <li key={s.num} aria-current={step === s.num ? "step" : undefined} className="flex flex-shrink-0 items-center gap-2">
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                    step >= s.num ? "bg-fi-amber text-fi-amber-ink" : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted"
                  }`}
                >
                  {s.num}
                </span>
                <span className={`whitespace-nowrap text-xs font-semibold ${step >= s.num ? "text-fieldiq-text" : "text-fieldiq-muted"}`}>{s.label}</span>
                {i < steps.length - 1 && <span aria-hidden="true" className={`h-px w-6 sm:w-12 ${step > s.num ? "bg-fi-amber" : "bg-fieldiq-border"}`} />}
              </li>
            ))}
          </ol>

          <div className="max-w-3xl rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card p-4 sm:p-6">
            {step === 1 && (
              <div className="space-y-6">
                {/* Load Type */}
                <div>
                  <label htmlFor="nl-load-type" className={labelCls}>Load Type <span className="text-fieldiq-text">*</span></label>
                  <select id="nl-load-type" value={form.loadType} onChange={(e) => update("loadType", e.target.value)} className={selectCls}>
                    {LOAD_TYPE_OPTIONS.map((lt) => (
                      <option key={lt.value} value={lt.value}>{lt.label} — {lt.desc}</option>
                    ))}
                  </select>
                </div>

                {/* Add New Customer Modal */}
                {showAddCustomer && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-sm rounded-fi-panel border border-fieldiq-border bg-fieldiq-card shadow-2xl">
                      <div className="flex items-center justify-between border-b border-fieldiq-border px-5 py-4">
                        <h3 className="text-lg font-bold text-fieldiq-text">Add New Customer</h3>
                        <button type="button" aria-label="Close" onClick={() => setShowAddCustomer(false)} className="text-fieldiq-muted hover:text-fieldiq-text"><X size={20} /></button>
                      </div>
                      <div className="px-5 py-4">
                        <input
                          placeholder="Company name"
                          aria-label="Company name"
                          value={newCustomerName}
                          onChange={(e) => setNewCustomerName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddCustomer()}
                          autoFocus
                          className={inputCls}
                        />
                      </div>
                      <div className="flex gap-3 border-t border-fieldiq-border px-5 py-4">
                        <button type="button" onClick={() => setShowAddCustomer(false)} className="flex-1 rounded-fi-control border border-fieldiq-border py-2.5 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50">Cancel</button>
                        <button type="button" onClick={handleAddCustomer} disabled={!newCustomerName.trim() || savingCustomer} className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-50">
                          {savingCustomer && <Loader2 size={14} className="animate-spin" />}
                          {savingCustomer ? 'Adding...' : 'Add Customer'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Customer + AFE */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nl-customer" className={labelCls}>Customer <span className="text-fieldiq-text">*</span></label>
                    <div className="flex items-center gap-2">
                      {/*
                        The VALUE is the customer's UUID. `POST /trucking/loads`
                        resolves `customerId` against `customers` and 404s on
                        anything else, so the name this used to hold could never
                        have been sent — which nothing noticed, because nothing
                        was ever sent.
                      */}
                      <select id="nl-customer" value={form.customerId} onChange={(e) => update("customerId", e.target.value)} className={selectCls + " flex-1"}>
                        <option value="">Select customer</option>
                        {customers.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name || c.companyName || c.id}</option>
                        ))}
                      </select>
                      <Tooltip content="Add new customer" className="shrink-0">
                        <button type="button" onClick={() => setShowAddCustomer(true)} aria-label="Add new customer" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-fi-control border border-dashed border-fieldiq-border text-fieldiq-muted transition-colors hover:border-fi-amber hover:text-fieldiq-text">
                          <Plus className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="nl-afe" className={labelCls}>AFE / Job Number</label>
                    <input id="nl-afe" className={inputCls} placeholder="e.g. AFE-2026-044" maxLength={100} value={form.afeNumber} onChange={(e) => update("afeNumber", e.target.value)} />
                  </div>
                </div>

                {/* Pickup + Delivery */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nl-pickup" className={labelCls}>Pickup Location <span className="text-fieldiq-text">*</span></label>
                    <input id="nl-pickup" className={inputCls} placeholder="Drayton Valley yard" value={form.pickupLocation} onChange={(e) => update("pickupLocation", e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="nl-delivery" className={labelCls}>Delivery LSD <span className="text-fieldiq-text">*</span></label>
                    <input id="nl-delivery" className={inputCls} placeholder="e.g. 10-22-076-20W5" value={form.deliveryLsd} onChange={(e) => update("deliveryLsd", e.target.value)} />
                  </div>
                </div>

                {/* Date + Time + Duration */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label htmlFor="nl-date" className={labelCls}>Date <span className="text-fieldiq-text">*</span></label>
                    <input id="nl-date" type="date" className={inputCls} value={form.date} onChange={(e) => update("date", e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="nl-departure" className={labelCls}>Departure Time <span className="text-fieldiq-text">*</span></label>
                    <select id="nl-departure" value={form.departureTime} onChange={(e) => update("departureTime", e.target.value)} className={selectCls}>
                      <option value="">Choose…</option>
                      {departureTimes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="nl-duration" className={labelCls}>Est. Duration (hrs) <span className="text-fieldiq-text">*</span></label>
                    <select id="nl-duration" value={form.duration} onChange={(e) => update("duration", e.target.value)} className={selectCls}>
                      <option value="">Choose…</option>
                      {durations.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>

                {/* Special Instructions */}
                <div>
                  <label htmlFor="nl-instructions" className={labelCls}>Special Instructions</label>
                  <textarea
                    id="nl-instructions"
                    className="w-full resize-none rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                    placeholder="H2S area, access card required, etc."
                    value={form.specialInstructions}
                    onChange={(e) => update("specialInstructions", e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <p className={labelCls}>Cargo Manifest</p>
                  <div className="mt-3 space-y-3">
                    {form.cargoItems.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_auto] items-end gap-2 sm:grid-cols-[1fr_140px_auto] sm:gap-3">
                        <div>
                          <label htmlFor={`nl-cargo-${idx}`} className="mb-1 block text-[10px] text-fieldiq-muted">Item description</label>
                          <input id={`nl-cargo-${idx}`} className={inputCls} placeholder="e.g. Drawworks assembly" value={item.item}
                            onChange={(e) => {
                              const items = [...form.cargoItems];
                              items[idx] = { ...items[idx], item: e.target.value };
                              setForm((prev) => ({ ...prev, cargoItems: items }));
                            }}
                          />
                        </div>
                        <div>
                          <label htmlFor={`nl-weight-${idx}`} className="mb-1 block text-[10px] text-fieldiq-muted">Weight</label>
                          <input id={`nl-weight-${idx}`} className={inputCls} placeholder="e.g. 18,400 kg" value={item.weight}
                            onChange={(e) => {
                              const items = [...form.cargoItems];
                              items[idx] = { ...items[idx], weight: e.target.value };
                              setForm((prev) => ({ ...prev, cargoItems: items }));
                            }}
                          />
                        </div>
                        <button type="button" aria-label={`Remove cargo item ${idx + 1}`} onClick={() => {
                          const items = form.cargoItems.filter((_, i) => i !== idx);
                          setForm((prev) => ({ ...prev, cargoItems: items.length ? items : [{ item: "", weight: "" }] }));
                        }} className="flex h-11 items-center rounded-fi-control border border-fieldiq-border px-3 text-fieldiq-muted transition-colors hover:text-fieldiq-text">
                          <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, cargoItems: [...prev.cargoItems, { item: "", weight: "" }] }))}
                      className="text-xs font-bold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
                    >
                      + Add cargo item
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {/*
                  A fleet unit (api#860 moved `loads.truck_id` to `trucking_fleet`).
                  Optional: a load can be booked unassigned and given a truck later.
                  An out-of-service unit is listed but cannot be chosen; the API
                  refuses it with a 409 (see FleetUnitPicker).
                */}
                <label htmlFor="new-load-truck" className={labelCls}>Select Truck (optional)</label>
                <FleetUnitPicker
                  id="new-load-truck"
                  purpose="load"
                  kind="truck"
                  value={form.truckId}
                  onChange={(v) => update("truckId", v)}
                  className={selectCls}
                />
                <div>
                  <label htmlFor="new-load-trailer" className={labelCls}>Select Trailer (optional)</label>
                  <FleetUnitPicker
                    id="new-load-trailer"
                    purpose="load"
                    kind="trailer"
                    value={form.trailerId}
                    onChange={(v) => update("trailerId", v)}
                    className={selectCls}
                  />
                </div>
                <div>
                  {/*
                    The driver (fe#815). Optional: a load can be booked with no
                    driver and given one on its BOL page later. An expired
                    licence is flagged in the list and still choosable — the API
                    decides, and its refusal is shown as sent.
                  */}
                  <label htmlFor="new-load-driver" className={labelCls}>Select Driver (optional)</label>
                  <DriverPicker
                    id="new-load-driver"
                    value={form.driverId}
                    onChange={(v) => update("driverId", v)}
                    className={selectCls}
                  />
                </div>
                <div className="border-t border-fieldiq-border pt-4">
                  <p className={labelCls}>Billing and rates</p>
                  <p className="mb-3 text-xs text-fieldiq-muted">Leave a box blank to use the rate card.</p>
                  <LoadRatesFields draft={rates} onChange={setRates} idPrefix="nl-rates" mode="create" />
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="new-load-confirm">
                <h3 className="mb-4 text-sm font-bold text-fieldiq-text">Booking Confirmation</h3>
                <dl className="space-y-2.5 text-sm">
                  {[
                    { label: "Type", value: getLoadTypeLabel(form.loadType) },
                    { label: "Customer", value: selectedCustomerName },
                    { label: "Truck", value: !form.truckId ? "Unassigned" : selectedTruck ? fleetUnitLabel(selectedTruck) : "—" },
                    { label: "Trailer", value: !form.trailerId ? "No trailer" : selectedTrailer ? fleetUnitLabel(selectedTrailer) : "—" },
                    { label: "Driver", value: !form.driverId ? "Unassigned" : selectedDriver ? driverOptionLabel(selectedDriver) : "—" },
                    { label: "From", value: form.pickupLocation || "—" },
                    { label: "To", value: form.deliveryLsd || "—" },
                    { label: "Date", value: dateLabel },
                    { label: "Departure", value: form.departureTime || "—" },
                    { label: "Duration", value: form.duration || "—" },
                    { label: "Billing", value: BILLING[rates.billingType].label },
                    { label: BILLING[rates.billingType].rateLabel, value: rates.haulRate.trim() || "From the rate card" },
                    { label: "Province of the work", value: rates.provinceState ? regionName(rates.provinceState) : "—" },
                  ].map(row => (
                    <div key={row.label} className="flex justify-between gap-4">
                      <dt className="text-fieldiq-muted">{row.label}</dt>
                      <dd className="text-right font-medium text-fieldiq-text">{row.value}</dd>
                    </div>
                  ))}
                  {form.cargoItems.filter(c => c.item).length > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-fieldiq-muted">Cargo items</dt>
                      <dd className="font-medium text-fieldiq-text">{form.cargoItems.filter(c => c.item).length}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Navigation */}
            <div className="mt-8 flex flex-col gap-3 border-t border-fieldiq-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-semibold text-fieldiq-muted">
                Step {step} of {steps.length} — {steps[step - 1].label}
              </p>
              <div className="flex gap-2">
                {step > 1 && (
                  <button type="button" onClick={() => setStep(step - 1)} className="rounded-fi-control border border-fieldiq-border px-4 py-2.5 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50">
                    Back
                  </button>
                )}
                {step < 4 ? (
                  <button type="button" onClick={() => setStep(step + 1)} className="fi-action fi-action--standard fi-action--primary">
                    {step === 1 ? "Continue to cargo" : step === 2 ? "Continue to truck" : "Review & confirm"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={booking || !form.departureTime || !form.duration}
                    className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
                  >
                    {booking && <Loader2 size={14} className="animate-spin" />}
                    {booking ? "Booking…" : "Confirm booking"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewLoadBooking;
